import { useRecoilState } from "recoil";
import { productKeyAtom, userInfoAtom } from "src/atoms";
import { dateFormatUtc } from "src/utils/dateManagement";
import "./Console.style.scss";
import { useProductFlags } from "src/hooks/recoil/useProductFlags";
import {
  ConsoleCashSlice,
  ConsoleServiceSlice,
  ConsoleSiteSlice,
  ConsoleViewSlice,
} from "./useConsoleController";
import ConfirmRenameModal from "./modals/ConfirmRenameModal";
import ConfirmDeleteModal from "./modals/ConfirmDeleteModal";
import CashStatusPanel from "./parts/CashStatusPannel";
import { BANK_INFO, getServiceUrl, shouldShowDeleteButton } from "./utils";
import EmptyState from "./parts/EmptyState";
import NoticeBanner from "./parts/NoticeBanner";
import ServiceCards from "./parts/ServiceCards";
import ServiceCreationStates from "./parts/ServiceCreationStates";
import CreateProgressBanner from "./parts/CreateProgressBanner";
import ServiceDetail from "./parts/ServiceDetail";
import { useComputePromotionNotice } from "src/hooks/validation/console/useComputePromotionNotice";
import { ProductKey } from "src/config/config";
import { useRouter } from "src/hooks/useRouter";

// 리팩토링용 콘솔 UI — 컨테이너가 전달한 도메인 그룹(view/site/service/cash)만 소비
interface IConsoleUIProps {
  view: ConsoleViewSlice;
  site: ConsoleSiteSlice;
  service: ConsoleServiceSlice;
  cash: ConsoleCashSlice;
}
const ConsoleUI = ({ view, site, service, cash }: IConsoleUIProps) => {
  const {
    serviceList,
    siteNxacList,
    detailIndex,
    viewType,
    makeType,
    serviceNotice,
    createPercent,
    isUnregister,
    isDone,
    closeCreateMsg,
  } = view;
  const { newSite, makePaySite, makePromotionSite, openEco, openEcoAdmin } =
    site;
  const {
    modal,
    newServiceName,
    handleChange,
    toggleModalConfirmServerName,
    toggleModalConfirmDelete,
    confirmServiceName,
    confirmServiceDelete,
  } = service;
  const { userCashStatus, showCashReqModal, requestCashStatus } = cash;

  const [productKey] = useRecoilState(productKeyAtom);
  const [userInfo] = useRecoilState(userInfoAtom);

  const { isOciProduct } = useProductFlags();
  const { openWrapsody, openMindSat } = useRouter();
  // 공통 파생값
  const current = serviceList[detailIndex];

  // 사이트 관리 배너 메세지
  const promotionNotice = useComputePromotionNotice({
    serviceList,
    serviceEndTimeText: current?.serviceEndTime,
    subscriptionEndDateText: current?.subscriptionEndDate,
    isOciProduct: isOciProduct,
    productKey: productKey as ProductKey,
  });
  // 조기 반환 1: 로딩 중
  if (!isDone) return <div className="content" />;

  // 조기 반환 2: 현금 결제 플로우 (기존 로직 유지: 필요 시 상위에서 bool 전달)
  const isCashApply = false; // 기존과 동일하게 상수 처리. 필요 시 props로 승격하세요.
  // 현금 결제 플로우 처리
  if (isCashApply) {
    return (
      <CashStatusPanel
        userCashStatus={userCashStatus!}
        onRequest={() => requestCashStatus("APPLY")}
        onCancel={showCashReqModal}
        bankInfo={BANK_INFO}
      />
    );
  }

  // 서비스 목록이 비었을 때
  // if ((serviceList?.length ?? 0) === 0) {
  // 등록 안된 서비스 있을 때
  if (isUnregister) {
    // 사이트 만들기 화면
    return <EmptyState onCreate={newSite} />;
  }
  // goSiteType();
  //   return null;
  // }

  // 목록 뷰
  if (viewType === "list") {
    return (
      <div className={"content"}>
        {!!promotionNotice && (
          <NoticeBanner isEnding={current?.payStatus === "ENDING"} />
        )}

        {makeType === "T_APPLY" && <NoticeBanner onAction={newSite} />}

        {serviceList.length > 0 ? (
          <ServiceCards
            items={serviceList}
            // onClickItem={goDetail}
            getUrl={(s) =>
              getServiceUrl(productKey, isOciProduct, s.domainName)
            }
            dateFormatUtc={(n) => dateFormatUtc(n?.toString())}
          />
        ) : (
          <ServiceCreationStates
            makeType={makeType as any}
            onMakePay={makePaySite}
            onMakePromotion={makePromotionSite}
          // onMakeTrial={makeTrialSite} // 무료 체험 관련 로직 필요시 추후 개발
          />
        )}
      </div>
    );
  }

  // 상세 뷰 (기본)
  if (viewType === "detail" && current) {
    const canDelete = shouldShowDeleteButton({
      billType: current.billType as any,
      serviceEndTime: current.serviceEndTime,
      serviceNotice: serviceNotice as any,
      nextPayDate: current.nextPayDate,
    });

    return (
      <div className="content">
        {!!promotionNotice && ( // 사이트가 삭제 될 예정입니다.
          <NoticeBanner
            isEnding={current?.payStatus === "ENDING"}
            text={promotionNotice}
          />
        )}

        {makeType === "T_APPLY" && ( // 유료로 전환하시겠습니까?
          <NoticeBanner onAction={newSite} />
        )}
        {createPercent > 0 &&
          !isOciProduct && ( // 사이트 생성 프로그레스바
            <CreateProgressBanner
              percent={createPercent}
              notice={serviceNotice as any} // 사이트 생성 프로그레스바 메시지
              onClose={closeCreateMsg}
            />
          )}

        <ServiceDetail // 서비스 상세정보
          current={current}
          siteNxacList={siteNxacList}
          userLastLogin={dateFormatUtc(userInfo?.loginUnixTime)}
          dateFormatUtc={(n) => dateFormatUtc(n)}
          onOpenUserPage={
            isOciProduct && productKey === "WRAPSODY"
              ? openWrapsody
              : productKey === "MIND_SAT"
                ? () => openMindSat({ domainName: current?.domainName })
                : openEco
          }
          onOpenAdminPage={
            !isOciProduct && productKey === "WRAPSODY_ECO"
              ? openEcoAdmin
              : undefined
          }
          canDelete={canDelete}
          onClickDelete={toggleModalConfirmDelete}
        />

        {/* 모달 */}
        {modal?.confirmServiceNameIsActive && ( // 서비스 이름 변경 모달
          <ConfirmRenameModal
            show={modal.confirmServiceNameIsActive}
            newName={newServiceName}
            onChange={handleChange}
            onClose={toggleModalConfirmServerName}
            onConfirm={confirmServiceName}
          />
        )}
        {modal?.confirmDeleteIsActive && ( // 서비스 삭제 모달
          <ConfirmDeleteModal
            show={modal.confirmDeleteIsActive}
            onClose={toggleModalConfirmDelete}
            onConfirm={confirmServiceDelete}
          />
        )}
      </div>
    );
  }

  return null;
};

export default ConsoleUI;
